import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography, styled } from '@mui/material';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import InteractiveTable from 'components/InteractiveTable';
import Heading from 'components/Heading';
import { sortAllocations } from '../utils/sortAllocations';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(8),
}));

interface TransferLinkedCapitalActionCardProps {
  capitalAction: LinkedCapitalAction;
  index: number;
  isLastElement: boolean;
  baseCurrency: string;
  transferDate: CalendarDay;
}

const CapitalActionName: React.FC<{
  name: string;
  capitalActionHeader: {
    investments: number;
    expenses: number;
    capitalRefunds: number;
    fees: number;
    additionalPayments: number;
  };
}> = ({ name, capitalActionHeader }) => {
  const items = [
    { count: capitalActionHeader?.investments, label: 'investment' },
    { count: capitalActionHeader?.expenses, label: 'expense' },
    { count: capitalActionHeader?.capitalRefunds, label: 'capital refund' },
    { count: capitalActionHeader?.fees, label: 'fee' },
    {
      count: capitalActionHeader?.additionalPayments,
      label: 'additional payment',
    },
  ];

  const subtitle = items
    .filter((item) => (item.count ?? 0) > 0)
    .map((item) => `${item.count} ${pluralize(item.label, item.count)}`)
    .join(', ');

  return (
    <Box>
      <Typography variant="body1" key="capital-action-name">
        {name ?? ''}
      </Typography>
      <Typography variant="body1" key="capital-action-header">
        {subtitle}
      </Typography>
    </Box>
  );
};

const TransferLinkedCapitalActionCard: React.FC<
  TransferLinkedCapitalActionCardProps
> = ({ capitalAction, index, isLastElement, baseCurrency, transferDate }) => {
  const [activeTab, setActiveTab] = useState(1);

  const sortedOriginalBreakdown = sortAllocations(
    capitalAction.originalInvestorBreakdown,
  );
  const sortedNewBreakdown = sortAllocations(
    capitalAction.newInvestorBreakdown,
  );

  const rows =
    activeTab === 0
      ? sortedOriginalBreakdown.map((row) => ({
          ...row,
          callOverpaidOrUnderpaid: 0,
        }))
      : sortedNewBreakdown;

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const columns = [
    {
      field: 'investorName',
      headerName: 'Name',
      renderCell: ({ row }) => `${row.investorName} `,
      flex: 1,
    },
    {
      field: 'investments',
      headerName: 'Call - Investments',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'expenses',
      headerName: 'Call - Expenses',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'fees',
      headerName: 'Call - Fees',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.fees, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'returnOfCapital',
      headerName: 'Capital refunds',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'netCallOrDistribution',
      headerName: 'Net call/(distribution)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    ...(activeTab === 1
      ? [
          {
            field: 'callOverpaidOrUnderpaid',
            headerName: 'Call underpaid/(overpaid)',
            renderCell: ({ row }) =>
              numberToCurrencyString(row.callOverpaidOrUnderpaid, {
                fallback: '-',
                currency: row.baseCurrency,
                negativeInBrackets: true,
              }),
            flex: 1,
          },
        ]
      : []),
  ];

  return (
    <EqualisationsConcertina
      key={index}
      gridTemplateColumns="60% 20% 18% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            <Tab
              label={
                <Heading variant="h5">
                  Allocation before {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
            <Tab
              label={
                <Heading variant="h5">
                  Allocation after {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
          </StyledTabs>
          <InteractiveTable
            id="investor-breakdown-table"
            columns={columns}
            rows={rows}
            scrollable={true}
          />
        </>
      }
      concertinaItems={[
        <CapitalActionName
          name={capitalAction?.name ?? ''}
          capitalActionHeader={capitalAction.capitalActionHeader ?? {}}
          key="capital-action-name"
        />,
        <Typography variant="body1" key="ledger-date">
          {capitalAction?.ledgerDate
            ? capitalAction.ledgerDate.toLocalDateLabel()
            : '-'}
        </Typography>,
        <Typography variant="body1" key="total-amount">
          {numberToCurrencyString(capitalAction.totalAmountLcy, {
            fallback: '-',
            currency: baseCurrency,
            negativeInBrackets: true,
          })}
        </Typography>,
      ]}
    />
  );
};

export default TransferLinkedCapitalActionCard;
